import type {
  EmbryoGenomicResult,
  EmbryoReport,
} from "@src/packages/cycles/hooks/useEmbryoReport";
import type { Specimen } from "../useSpecimens";
import {
  getEmbryoHistory,
  type EmbryoHistoryEvent,
} from "./embryoTestingOverview";

export type EmbryoTimelineEvent =
  | EmbryoHistoryEvent
  | {
      kind: "development";
      observation: EmbryoGenomicResult["developmentObservations"][number];
    };

/** Identify a fertilization check from reported findings, not its day number. */
export function isFertilizationObservation(
  observation: Pick<
    EmbryoGenomicResult["developmentObservations"][number],
    "pronucleiCount" | "polarBodyCount"
  >,
) {
  return (
    observation.pronucleiCount !== null || observation.polarBodyCount !== null
  );
}

function calendarDay(date: string | null | undefined) {
  if (!date) return null;
  const value = Date.parse(`${date.slice(0, 10)}T00:00:00.000Z`);
  return Number.isFinite(value) ? value : null;
}

/**
 * One presentation timeline, preserving the API's observation records.
 * Observation audit timestamps are not observation dates. A biopsy's reported
 * day/date (or the embryo's own creation) positions days between milestones,
 * but those ordering coordinates never become displayed calendar dates.
 */
export function getEmbryoTimeline(
  report: Pick<EmbryoReport, "retrievalDate" | "reportDate">,
  embryo: Pick<
    EmbryoGenomicResult,
    "biopsyDate" | "biopsyDay" | "result" | "developmentObservations"
  >,
  specimen: Specimen | null,
): EmbryoTimelineEvent[] {
  const history = getEmbryoHistory(report, embryo, specimen);
  const development = [...embryo.developmentObservations]
    .sort((left, right) => left.day - right.day)
    .map((observation) => ({ kind: "development" as const, observation }));
  if (development.length === 0) return history;

  const biopsyDate = calendarDay(embryo.biopsyDate);
  const creationDate = calendarDay(specimen?.collectedAt);
  const reference =
    biopsyDate !== null && embryo.biopsyDay !== null
      ? { date: biopsyDate, day: embryo.biopsyDay }
      : creationDate !== null
        ? { date: creationDate, day: 0 }
        : null;

  if (reference === null) {
    // Without a developmental date anchor, keep a day-ordered group after
    // retrieval/creation. Never date frozen-egg development from retrieval.
    const firstMilestone = history.findIndex(
      (event) => event.kind !== "retrieved" && event.kind !== "created",
    );
    const index = firstMilestone < 0 ? history.length : firstMilestone;
    return [
      ...history.slice(0, index),
      ...development,
      ...history.slice(index),
    ];
  }

  const dated = history.map((event) => ({
    event,
    position: calendarDay(event.date) ?? reference.date,
    priority: event.kind === "retrieved" || event.kind === "created" ? 0 : 2,
  }));
  const days = development.map((event) => ({
    event,
    position:
      reference.date + (event.observation.day - reference.day) * 86_400_000,
    priority: 1,
  }));
  return [...dated, ...days]
    .sort(
      (left, right) =>
        left.position - right.position || left.priority - right.priority,
    )
    .map(({ event }) => event);
}
